import {
  faEdit,
  faLocationDot,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useState } from "react";
import { Skeleton } from "@mui/material";
import { IAddressData } from "src/types/GlobalInterfaces";
import httpRequest from "src/helpers/httpRequest";
import { toast } from "react-toastify";
import { Loader, Loader2 } from "lucide-react";
import { setCurrentAddress } from "src/Store/cartSlice";
import { useDispatch } from "react-redux";
import { AppDispatch } from "src/Store/store";
import { handleAddressSelect } from "src/helpers/helperFunctions";

const LabelWithIcon = ({
  icon,
  label,
  className,
  onClick,
  id,
  skeleton = false,
  loading = false,
}: {
  icon: any;
  label: string;
  className?: string;
  onClick?: () => void;
  id?: string;
  skeleton?: boolean;
  loading?: boolean;
}) => {
  return (
    <div
      className="flex items-center gap-1 cursor-pointer"
      onClick={(e) => {
        e.stopPropagation();
        if (loading) return;
        onClick?.();
      }}
      id={id}
    >
      {skeleton ? (
        <>
          <Skeleton variant="circular" width={15} height={15} />
          <Skeleton variant="text" width={50} height={20} />
        </>
      ) : (
        <>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <FontAwesomeIcon icon={icon} className="font-size-15px" />
          )}
          <p
            className={`font-Poppins-Regular font-size-15px mt-[2px] text-[#303030] ${className}`}
          >
            {label}
          </p>
        </>
      )}
    </div>
  );
};

const AddressDetails = ({
  label,
  value,
  skeleton = false,
}: {
  label: string;
  value: string;
  skeleton?: boolean;
}) => {
  return (
    <div className="flex gap-2">
      {skeleton ? (
        <>
          <Skeleton variant="text" width="25%" height={20} />
          <Skeleton variant="text" width="75%" height={20} />
        </>
      ) : (
        <>
          <p className="font-Poppins-Regular font-size-15px text-[#5B5B5B] w-[25%]">
            {label}
          </p>
          <p className="font-Poppins-Medium font-size-15px text-[#303030] w-[75%] line-clamp-2">
            {value}
          </p>
        </>
      )}
    </div>
  );
};

const AddressCard = ({
  index,
  address,
  onEdit,
  onDelete,
  onSelect,
  isActive = false,
  id = "",
  editId = "",
  deleteId = "",
  skeleton = false,
  setAllAddresses,
  allAddresses,
}: {
  index?: number;
  address: IAddressData;
  onEdit: () => void;
  onDelete: () => void;
  onSelect: () => void;
  isActive?: boolean;
  id?: string;
  editId?: string;
  deleteId?: string;
  skeleton?: boolean;
  setAllAddresses?: (addresses: any) => any;
  allAddresses?: any;
}) => {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch<AppDispatch>();

  const handleDelete = async () => {
    setLoading(true);
    const { err, res } = await httpRequest<{ message: string }>({
      path: `/customer-address/delete-customer-address/${address._id}`,
      method: "delete",
      params: {}
    });
    if (!err) {
      toast.success("Address deleted successfully");
      let newAddresses = allAddresses?.filter(
        (item: any) => item._id !== address._id
      );
      setAllAddresses?.(newAddresses);
      let newAddress = newAddresses?.length > 0 ? newAddresses[0] : null;
      if (newAddress) {
        let isSelected = await handleAddressSelect(newAddress._id , false);
        if (isSelected) {
          dispatch(setCurrentAddress(newAddress));
        }
      } else {
        dispatch(setCurrentAddress(null));
      }
      onDelete();
    }
    setLoading(false);
  };

  return (
    <div
      className={`w-full h-fit ${
        skeleton
          ? "bg-white border border-gray-300"
          : isActive
          ? "bg-[#f0fdf4] border border-[#22C55E]"
          : "bg-white border border-gray-300"
      } rounded-lg cursor-pointer 2xl:rounded-xl res-padd`}
      onClick={skeleton ? undefined : onSelect}
      id={index || index === 0 ? `address-card-${index + 1}` : id || ""}
    >
      <div className="w-full res-padd-bottom border-b border-gray-300 flex items-center justify-between">
        {skeleton ? (
          <div className="flex items-center gap-2">
            <Skeleton variant="circular" width={20} height={20} />
            <Skeleton variant="text" width={100} height={24} />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <FontAwesomeIcon icon={faLocationDot} size="lg" />
            <p className="font-Poppins-Medium font-size-20px theme-color-green-dark">
              {address?.addressType?.charAt(0).toUpperCase() +
                address?.addressType?.slice(1)}
            </p>
          </div>
        )}
        <div className="flex items-center gap-5">
          <LabelWithIcon
            icon={faEdit}
            label="Edit"
            onClick={skeleton ? undefined : onEdit}
            id={index || index === 0 ? `address-card-edit-${index + 1}` : editId || ""}
            skeleton={skeleton}
          />
          <LabelWithIcon
            icon={faTrash}
            label="Delete"
            onClick={skeleton ? undefined : handleDelete}
            id={index || index === 0 ? `address-card-delete-${index + 1}` : deleteId || ""}
            skeleton={skeleton}
            loading={loading}
          />
        </div>
      </div>
      <div className="w-full flex flex-col gap-2 res-padd-top">
        <AddressDetails
          label="Name"
          value={address?.personName}
          skeleton={skeleton}
        />
        <AddressDetails
          label="Address"
          value={`${address?.streetAddress}`}
          skeleton={skeleton}
        />
        <AddressDetails
          label="Mobile Number"
          value={address?.mobileNumber}
          skeleton={skeleton}
        />
      </div>
    </div>
  );
};

export default AddressCard;
